import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { UiformBaseComponent } from '../uiform-base/uiform-base.component';
import { ServiceLocatorService } from '@ncs/ncs-lib';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TaskEditModalComponent } from './task-edit-modal/task-edit-modal.component';

// ─── Inline Confirm Dialog (no separate file needed) ──────────────────────────
@Component({
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div>
      <div
        class="d-flex align-items-center gap-2 px-3 pt-3 pb-2 border-bottom"
        [style.background]="data.type === 'danger' ? '#fff5f5' : '#fffbf0'"
      >
        <mat-icon
          [style.color]="data.type === 'danger' ? '#dc3545' : '#fd7e14'"
          style="font-size:22px;width:22px;height:22px"
        >
          {{ data.type === 'danger' ? 'delete_forever' : 'refresh' }}
        </mat-icon>
        <h6 class="mb-0 fw-semibold">{{ data.title }}</h6>
      </div>
      <mat-dialog-content class="px-3 py-3">
        <p class="mb-0 text-secondary" style="font-size:0.92rem">
          {{ data.message }}
        </p>
      </mat-dialog-content>
      <mat-dialog-actions class="px-3 pb-3 gap-2 justify-content-end">
        <button mat-stroked-button (click)="ref.close(false)">
          <mat-icon style="font-size:16px;width:16px;height:16px"
            >close</mat-icon
          >
          Cancel
        </button>
        <button
          mat-raised-button
          [color]="data.type === 'danger' ? 'warn' : 'primary'"
          (click)="ref.close(true)"
        >
          <mat-icon style="font-size:16px;width:16px;height:16px">
            {{ data.type === 'danger' ? 'delete' : 'check' }}
          </mat-icon>
          {{ data.confirmText || 'Confirm' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
})
class ConfirmDialogComponent {
  constructor(
    public ref: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      message: string;
      confirmText?: string;
      type?: 'danger' | 'warning';
    },
  ) {}
}

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Task {
  id: string;
  title: string;
  description: string;
  status: string;
  dueDate?: Date;
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignee?: string;
  labels?: string[];
  createdAt: Date;
  updatedAt: Date;
  order: number;
}

interface Column {
  id: string;
  title: string;
  tasks: Task[];
  order: number;
  color?: string;
  createdAt: Date;
}

interface Board {
  id: string;
  title: string;
  columns: Column[];
  createdAt: Date;
  updatedAt: Date;
}

interface FilterOptions {
  searchText: string;
  priority: string;
  assignee: string;
  labels: string[];
  dueDateRange: {
    start: Date | null;
    end: Date | null;
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-task-board',
  templateUrl: './task-board.component.html',
  styleUrls: ['./task-board.component.scss'],
})
export class TaskBoardComponent extends UiformBaseComponent {
  board: Board;
  newColumnTitle = '';
  isAddingColumn = false;
  isLoading = false;

  // Filter properties
  filterOptions: FilterOptions = {
    searchText: '',
    priority: 'all',
    assignee: 'all',
    labels: [],
    dueDateRange: { start: null, end: null },
  };

  // Search panel (separate from filter panel)
  showSearchPanel = false;
  searchOptions: FilterOptions = {
    searchText: '',
    priority: 'all',
    assignee: 'all',
    labels: [],
    dueDateRange: { start: null, end: null },
  };

  filteredTasks: { [columnId: string]: Task[] | undefined } = {};
  showFilterPanel = false;

  /** Resolved base URL: e.g. https://api.cuickdev.com/ctl/task */
  private taskEndpoint = '';

  // Default column definitions — used when DB has no data yet
  private readonly DEFAULT_COLUMNS: Omit<Column, 'tasks' | 'createdAt'>[] = [
    { id: 'todo-col', title: 'To Do', order: 0, color: '#4A90E2' },
    { id: 'inprogress-col', title: 'In Progress', order: 1, color: '#F5A623' },
    { id: 'review-col', title: 'Review', order: 2, color: '#7ED321' },
    { id: 'done-col', title: 'Done', order: 3, color: '#9013FE' },
  ];

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    super(locator, route);

    // Initialise an empty board so the template never crashes
    this.board = this.buildEmptyBoard();

    // Capture formId from route params if available
    if (route.snapshot.paramMap.get('id')) {
      this.uiformId = Number(route.snapshot.paramMap.get('id'));
    }
  }

  /** Cached list of user names fetched from /app-users/{appId} */
  private appUserNames: string[] = [];

  override ngOnInit(): void {
    super.ngOnInit();

    // Resolve the task endpoint from config then load data
    this.locator.config
      .getProperty('endpoint.task')
      .then((endpoint: string) => {
        this.taskEndpoint = endpoint;
        this.loadBoardFromDB();
        this.loadAppUserNames();
      });
  }

  // ─── Board Initialisation ──────────────────────────────────────────────────

  /** Returns an empty board skeleton using the default column set. */
  private buildEmptyBoard(): Board {
    return {
      id: 'default-board',
      title: 'My Task Board',
      columns: this.DEFAULT_COLUMNS.map((col) => ({
        ...col,
        tasks: [],
        createdAt: new Date(),
      })),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  // ─── DB Load ──────────────────────────────────────────────────────────────

  /**
   * Fetches all tasks from the backend (POST /search) and organises them
   * into the Kanban board columns.
   */
  loadBoardFromDB(): void {
    if (!this.taskEndpoint) return;

    this.isLoading = true;
    const url = `${this.taskEndpoint}/search/0`;

    this.locator.http.post(
      url,
      { pageSize: 500, formId: this.uiformId, appId: this.appId || undefined }, // fetch up to 500 tasks at once
      (res: any, error: boolean) => {
        this.isLoading = false;

        if (error || !res.success) {
          console.error('Task board: could not load tasks from DB', error);
          this.board = this.buildEmptyBoard();
          this.applyFilters();
          return;
        }

        const dbTasks: any[] = res.result?.data || [];
        this.board = this.buildBoardFromDbTasks(dbTasks);
        this.board.updatedAt = new Date();
        this.applyFilters();
        this.cdRef.detectChanges();
      },
    );
  }

  /**
   * Maps a flat list of DB task records into the Board model.
   * Each task's `status` field determines which column it belongs to.
   */
  private buildBoardFromDbTasks(dbTasks: any[]): Board {
    // Build a lookup: columnId → tasks
    const columnMap: { [colId: string]: Task[] } = {};
    this.DEFAULT_COLUMNS.forEach((col) => (columnMap[col.id] = []));

    dbTasks.forEach((raw) => {
      const task = this.mapDbTaskToTask(raw);
      const colId = this.statusToColumnId(task.status);

      if (!columnMap[colId]) {
        columnMap[colId] = []; // tolerate unknown status values
      }
      columnMap[colId].push(task);
    });

    // Sort tasks within each column by their `order` field
    Object.values(columnMap).forEach((tasks) =>
      tasks.sort((a, b) => a.order - b.order),
    );

    const columns: Column[] = this.DEFAULT_COLUMNS.map((col) => ({
      ...col,
      tasks: columnMap[col.id] || [],
      createdAt: new Date(),
    }));

    return {
      id: 'default-board',
      title: 'My Task Board',
      columns,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  /** Converts a raw DB record to the frontend Task model. */
  private mapDbTaskToTask(raw: any): Task {
    return {
      id: String(raw.id || raw._id || `task-${Date.now()}`),
      title: raw.title || 'Untitled',
      description: raw.description || '',
      status: raw.status || 'Not started',
      priority: this.normalisePriority(raw.priority),
      assignee: raw.assignee || raw.userName || '',
      labels: raw.labels
        ? Array.isArray(raw.labels)
          ? raw.labels
          : raw.labels.split(',').map((l: string) => l.trim())
        : [],
      dueDate: raw.targetDate ? new Date(raw.targetDate) : undefined,
      createdAt: raw.createdAt ? new Date(raw.createdAt) : new Date(),
      updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : new Date(),
      order: raw.order ?? 0,
    };
  }

  /** Maps DB `status` string → column id. */
  private statusToColumnId(status: string): string {
    const map: { [key: string]: string } = {
      'Not started': 'todo-col',
      todo: 'todo-col',
      'In Progress': 'inprogress-col',
      inprogress: 'inprogress-col',
      Review: 'review-col',
      review: 'review-col',
      Completed: 'done-col',
      done: 'done-col',
    };
    return map[status] || 'todo-col';
  }

  /** Maps column id → DB `status` string. */
  private columnIdToStatus(columnId: string): string {
    const map: { [key: string]: string } = {
      'todo-col': 'Not started',
      'inprogress-col': 'In Progress',
      'review-col': 'Review',
      'done-col': 'Completed',
    };
    return map[columnId] || columnId.replace('-col', '');
  }

  /** Ensures priority is always one of the four valid values. */
  private normalisePriority(p: any): Task['priority'] {
    const valid = ['low', 'medium', 'high', 'critical'];
    const lc = (p || '').toLowerCase();
    return valid.includes(lc) ? (lc as Task['priority']) : 'medium';
  }

  // ─── DB Save / Update ─────────────────────────────────────────────────────

  /**
   * Saves a task to the backend.
   * Converts the frontend Task model back to the DB format expected by TasksCtl.
   */
  // private saveTaskToDB(
  //   task: Task,
  //   columnId: string,
  //   callback?: () => void,
  // ): void {
  //   if (!this.taskEndpoint) return;

  //   const status = this.columnIdToStatus(columnId);

  //   const payload: any = {
  //     title: task.title,
  //     description: task.description,
  //     status,
  //     priority: task.priority,
  //     assignee: task.assignee || '',
  //     labels: Array.isArray(task.labels)
  //       ? task.labels.join(',')
  //       : task.labels || '',
  //     targetDate: task.dueDate ? this.formatDateForDB(task.dueDate) : null,
  //     order: task.order,
  //   };

  //   // Include ID only when updating an existing record
  //   const numId = this.parseNumericId(task.id);
  //   if (numId && numId > 0) {
  //     payload.id = numId;
  //   }

  //   this.locator.http.post(
  //     `${this.taskEndpoint}/save`,
  //     payload,
  //     (res: any, error: boolean) => {
  //       if (error || !res?.success) {
  //         console.error('Task save failed', error, res);
  //         return;
  //       }

  //       // Update local task id with the DB-assigned id (important for new tasks)
  //       if (res.result?.data?.id) {
  //         task.id = String(res.result.data.id);
  //       }

  //       if (callback) callback();
  //       this.cdRef.detectChanges();
  //     },
  //   );
  // }
  private saveTaskToDB(
    task: Task,
    columnId: string,
    callback?: () => void,
  ): void {
    if (!this.taskEndpoint) return;

    const status = this.columnIdToStatus(columnId);

    const payload: any = {
      title: task.title,
      description: task.description,
      status,
      priority: task.priority,
      assignee: task.assignee || '',
      labels: Array.isArray(task.labels)
        ? task.labels.join(',')
        : task.labels || '',
      targetDate: task.dueDate ? this.formatDateForDB(task.dueDate) : null,
      order: task.order,
      formId: this.uiformId,
      appId: this.appId || null,
    };

    const numId = this.parseNumericId(task.id);
    if (numId && numId > 0) {
      payload.id = numId;
    }

    this.locator.http.post(
      `${this.taskEndpoint}/save`,
      payload,
      (res: any, error: boolean) => {
        if (error || !res?.success) {
          console.error('Task save failed', error, res);
          return;
        }
        if (res.result?.data?.id) {
          task.id = String(res.result.data.id);
        }
        if (callback) callback();
        this.cdRef.detectChanges();
      },
    );
  }
  /**
   * Calls the backend to update task status when dragged between columns.
   * Uses the dedicated endpoint: GET /ctl/task/status/{id}/{status}
   */
  private updateTaskStatusInDB(taskId: string, status: string): void {
    const numId = this.parseNumericId(taskId);
    if (!this.taskEndpoint || !numId) {
      console.warn('updateTaskStatusInDB: invalid taskId or endpoint', taskId);
      return;
    }

    const url = `${this.taskEndpoint}/status/${numId}/${encodeURIComponent(status)}`;
    this.locator.http.get(url, (res: any, error: boolean) => {
      if (error || !res?.success) {
        console.error('Status update failed', error, res);
      } else {
        this.cdRef.detectChanges();
      }
    });
  }

  /**
   * Calls the backend to update task priority.
   * Uses: GET /ctl/task/priority/{id}/{priority}
   */
  private updateTaskPriorityInDB(taskId: string, priority: string): void {
    const numId = this.parseNumericId(taskId);
    if (!this.taskEndpoint || !numId) return;

    const url = `${this.taskEndpoint}/priority/${numId}/${encodeURIComponent(priority)}`;
    this.locator.http.get(url, (res: any, error: boolean) => {
      if (error) console.error('Priority update failed', error);
    });
  }

  /**
   * Deletes a task from the backend.
   * Uses the base controller's DELETE endpoint: GET /ctl/task/delete/{id}
   */
  private deleteTaskFromDB(taskId: string, callback?: () => void): void {
    const numId = this.parseNumericId(taskId);
    if (!this.taskEndpoint || !numId) {
      if (callback) callback();
      return;
    }

    const url = `${this.taskEndpoint}/delete/${numId}`;
    this.locator.http.get(url, (res: any, error: boolean) => {
      if (error) console.error('Task delete failed', error);
      if (callback) callback();
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private parseNumericId(id: string): number | null {
    const n = Number(id);
    return isNaN(n) || n <= 0 ? null : n;
  }

  private formatDateForDB(date: Date): string {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // ─── App Users (for assignee filter) ─────────────────────────────────────

  /**
   * Fetches the accepted users for this application from the backend
   * (GET /ctl/task/app-users/{appId}) and caches their display names.
   * These names are used to populate the Assignee filter dropdown.
   */
  private loadAppUserNames(): void {
    const appId = this.appId;
    if (!appId || appId <= 0 || !this.taskEndpoint) return;

    const url = `${this.taskEndpoint}/app-users/${appId}`;
    this.locator.http.get(url, (res: any, error: boolean) => {
      if (error || !res?.success) {
        console.warn('TaskBoard: could not load app users for filter', error);
        return;
      }
      this.appUserNames = (res.result?.data || [])
        .map((u: any) => (u.userFirstname || '').trim())
        .filter((name: string) => !!name);
      this.cdRef.detectChanges();
    });
  }

  // ─── Reset ────────────────────────────────────────────────────────────────

  resetBoard(): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Reset Board',
          message:
            'Are you sure you want to reload the board from the database?',
          confirmText: 'Reset',
          type: 'warning',
        },
      })
      .afterClosed()
      .subscribe((ok: boolean) => {
        if (ok) this.loadBoardFromDB();
      });
  }

  // ─── Column Drag & Drop ───────────────────────────────────────────────────

  dropColumn(event: CdkDragDrop<Column[]>) {
    moveItemInArray(
      this.board.columns,
      event.previousIndex,
      event.currentIndex,
    );
    this.board.columns.forEach((col, index) => (col.order = index));
    this.applyFilters();
    // Column order is client-side only; persist task order updates via individual task saves if needed
  }

  // ─── Task Drag & Drop ─────────────────────────────────────────────────────

  dropTask(event: CdkDragDrop<Task[]>, column: Column) {
    if (event.previousContainer === event.container) {
      // ── Same column reorder ──────────────────────────────────────────────
      if (this.showFilterPanel) {
        const movedTask = event.item.data;
        const filteredTasks = this.filteredTasks[column.id] || [];
        moveItemInArray(filteredTasks, event.previousIndex, event.currentIndex);

        const actualIndex = column.tasks.findIndex(
          (t) => t.id === movedTask.id,
        );
        if (actualIndex > -1) {
          let targetIndex = 0;
          if (event.currentIndex > 0) {
            const prevTask = filteredTasks[event.currentIndex - 1];
            const prevActual = column.tasks.findIndex(
              (t) => t.id === prevTask.id,
            );
            if (prevActual > -1) targetIndex = prevActual + 1;
          }
          const [item] = column.tasks.splice(actualIndex, 1);
          column.tasks.splice(targetIndex, 0, item);
        }
      } else {
        moveItemInArray(column.tasks, event.previousIndex, event.currentIndex);
      }
      column.tasks.forEach((task, idx) => (task.order = idx));
    } else {
      // ── Cross-column move ─────────────────────────────────────────────────
      const sourceColumn = this.board.columns.find(
        (col) => col.id === event.previousContainer.id,
      );
      if (!sourceColumn) return;

      const movedTask = event.item.data as Task;
      const sourceIndex = sourceColumn.tasks.findIndex(
        (t) => t.id === movedTask.id,
      );
      if (sourceIndex === -1) return;

      const [removedTask] = sourceColumn.tasks.splice(sourceIndex, 1);

      // Update status to match destination column
      const newStatus = this.columnIdToStatus(column.id);
      removedTask.status = newStatus;
      removedTask.updatedAt = new Date();

      let targetIndex = event.currentIndex;

      if (this.showFilterPanel) {
        const filteredTarget = this.filteredTasks[column.id] || [];
        if (targetIndex < filteredTarget.length) {
          const targetTaskId = filteredTarget[targetIndex].id;
          const actualTarget = column.tasks.findIndex(
            (t) => t.id === targetTaskId,
          );
          if (actualTarget > -1) targetIndex = actualTarget;
        } else if (filteredTarget.length > 0) {
          const lastId = filteredTarget[filteredTarget.length - 1].id;
          const lastActual = column.tasks.findIndex((t) => t.id === lastId);
          targetIndex = lastActual > -1 ? lastActual + 1 : column.tasks.length;
        } else {
          targetIndex = column.tasks.length;
        }
      }

      column.tasks.splice(targetIndex, 0, removedTask);

      sourceColumn.tasks.forEach((task, idx) => (task.order = idx));
      column.tasks.forEach((task, idx) => (task.order = idx));

      // Persist status change to DB
      this.updateTaskStatusInDB(removedTask.id, newStatus);
    }

    if (this.showFilterPanel) {
      this.applyFilters();
    }
  }

  // ─── Add Task ─────────────────────────────────────────────────────────────

  addTask(columnId: string): void {
    const column = this.board.columns.find((c) => c.id === columnId);
    if (!column) return;

    const newTask: Task = {
      id: '', // will be set by DB after save
      title: 'New Task',
      description: '',
      status: this.columnIdToStatus(columnId),
      priority: 'medium',
      assignee: '',
      labels: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      order: column.tasks.length,
    };

    this.openEditTaskModal(newTask, columnId, true);
  }

  // ─── Edit Task ────────────────────────────────────────────────────────────

  editTask(task: Task, columnId: string): void {
    this.openEditTaskModal({ ...task }, columnId, false);
  }

  private openEditTaskModal(
    task: Task,
    columnId: string,
    isNewTask: boolean,
  ): void {
    const dialogRef = this.dialog.open(TaskEditModalComponent, {
      width: '500px',
      data: { task: { ...task }, isNewTask, appId: this.appId || 0 },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) return;

      const column = this.board.columns.find((c) => c.id === columnId);
      if (!column) return;

      if (isNewTask) {
        // Build the new task object
        const taskToSave: Task = {
          ...result,
          id: '',
          status: this.columnIdToStatus(columnId),
          createdAt: new Date(),
          updatedAt: new Date(),
          order: column.tasks.length,
        };

        // Save to DB first to get a real id
        this.saveTaskToDB(taskToSave, columnId, () => {
          column.tasks.push(taskToSave);
          this.applyFilters();
          this.cdRef.detectChanges();
        });
      } else {
        // Update existing task
        const taskIndex = column.tasks.findIndex((t) => t.id === task.id);
        if (taskIndex > -1) {
          const updatedTask: Task = {
            ...result,
            id: task.id,
            updatedAt: new Date(),
            order: column.tasks[taskIndex].order,
          };
          column.tasks[taskIndex] = updatedTask;

          this.saveTaskToDB(updatedTask, columnId, () => {
            this.applyFilters();
            this.cdRef.detectChanges();
          });
        }
      }
    });
  }

  // ─── Delete Task ──────────────────────────────────────────────────────────

  deleteTask(taskId: string, columnId: string): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Delete Task',
          message:
            'Are you sure you want to delete this task? This action cannot be undone.',
          confirmText: 'Delete',
          type: 'danger',
        },
      })
      .afterClosed()
      .subscribe((ok: boolean) => {
        if (!ok) return;
        const column = this.board.columns.find((c) => c.id === columnId);
        if (!column) return;

        // Remove from UI immediately for snappy UX
        column.tasks = column.tasks.filter((t) => t.id !== taskId);
        column.tasks.forEach((task, idx) => (task.order = idx));
        this.applyFilters();

        // Then delete from DB
        this.deleteTaskFromDB(taskId);
      });
  }

  // ─── Column Management (client-side only) ─────────────────────────────────

  addColumn(): void {
    if (!this.newColumnTitle.trim()) return;

    const newColumn: Column = {
      id: `col-${Date.now()}`,
      title: this.newColumnTitle,
      tasks: [],
      order: this.board.columns.length,
      color: this.getRandomColor(),
      createdAt: new Date(),
    };

    this.board.columns.push(newColumn);
    this.newColumnTitle = '';
    this.isAddingColumn = false;
    this.applyFilters();
  }

  editColumn(column: Column): void {
    const newTitle = prompt('Enter new column title:', column.title);
    if (newTitle && newTitle.trim()) {
      column.title = newTitle.trim();
    }
  }

  deleteColumn(columnId: string): void {
    const column = this.board.columns.find((c) => c.id === columnId);
    if (!column) return;

    const taskCount = column.tasks.length;
    const message =
      taskCount > 0
        ? `This column contains ${taskCount} task(s). All tasks will also be permanently deleted. Continue?`
        : `Are you sure you want to delete the "${column.title}" column?`;

    this.dialog
      .open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Delete Column',
          message,
          confirmText: 'Delete',
          type: 'danger',
        },
      })
      .afterClosed()
      .subscribe((ok: boolean) => {
        if (!ok) return;
        column.tasks.forEach((task) => this.deleteTaskFromDB(task.id));
        this.board.columns = this.board.columns.filter(
          (c) => c.id !== columnId,
        );
        this.applyFilters();
      });
  }

  toggleAddColumn(): void {
    this.isAddingColumn = !this.isAddingColumn;
    if (this.isAddingColumn) {
      setTimeout(() => {
        const input = document.querySelector(
          '.new-column-input',
        ) as HTMLInputElement;
        if (input) input.focus();
      }, 0);
    }
  }

  // ─── Filters ──────────────────────────────────────────────────────────────

  applyFilters(): void {
    this.filteredTasks = {};
    this.board.columns.forEach((column) => {
      this.filteredTasks[column.id] = column.tasks.filter((task) =>
        this.taskMatchesFilter(task),
      );
    });
    this.cdRef.detectChanges();
  }

  private taskMatchesFilter(task: Task): boolean {
    // ── Search panel: one input matches title, description OR assignee ────
    if (this.showSearchPanel && this.searchOptions.searchText) {
      const q = this.searchOptions.searchText.toLowerCase();
      const matchesTitle = task.title.toLowerCase().includes(q);
      const matchesDesc = task.description.toLowerCase().includes(q);
      const matchesAssignee = (task.assignee || '').toLowerCase().includes(q);
      if (!matchesTitle && !matchesDesc && !matchesAssignee) return false;
    }

    // ── Filter panel (unchanged) ──────────────────────────────────────────
    if (this.filterOptions.searchText) {
      const q = this.filterOptions.searchText.toLowerCase();
      if (
        !task.title.toLowerCase().includes(q) &&
        !task.description.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    if (
      this.filterOptions.priority !== 'all' &&
      task.priority !== this.filterOptions.priority
    ) {
      return false;
    }
    if (
      this.filterOptions.assignee !== 'all' &&
      task.assignee !== this.filterOptions.assignee
    ) {
      return false;
    }
    if (this.filterOptions.labels.length > 0) {
      if (!this.filterOptions.labels.some((lbl) => task.labels?.includes(lbl)))
        return false;
    }
    if (this.filterOptions.dueDateRange.start && task.dueDate) {
      if (new Date(task.dueDate) < this.filterOptions.dueDateRange.start)
        return false;
    }
    if (this.filterOptions.dueDateRange.end && task.dueDate) {
      const d = new Date(task.dueDate);
      d.setHours(23, 59, 59, 999);
      if (d > this.filterOptions.dueDateRange.end) return false;
    }
    return true;
  }

  onFilterChange(filterOptions: FilterOptions): void {
    this.filterOptions = filterOptions;
    this.applyFilters();
  }

  clearFilters(): void {
    this.filterOptions = {
      searchText: '',
      priority: 'all',
      assignee: 'all',
      labels: [],
      dueDateRange: { start: null, end: null },
    };
    this.applyFilters();
  }

  toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    if (this.showFilterPanel) this.applyFilters();
  }

  toggleSearchPanel(): void {
    this.showSearchPanel = !this.showSearchPanel;
    if (!this.showSearchPanel) {
      this.searchOptions = {
        searchText: '',
        priority: 'all',
        assignee: 'all',
        labels: [],
        dueDateRange: { start: null, end: null },
      };
      this.applyFilters();
    }
  }

  onSearchChange(opts: FilterOptions): void {
    this.searchOptions = opts;
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !(
      !this.filterOptions.searchText &&
      this.filterOptions.priority === 'all' &&
      this.filterOptions.assignee === 'all' &&
      this.filterOptions.labels.length === 0 &&
      !this.filterOptions.dueDateRange.start &&
      !this.filterOptions.dueDateRange.end
    );
  }

  // ─── Display Helpers ──────────────────────────────────────────────────────

  getColumnIds(): string[] {
    return this.board.columns.map((col) => col.id);
  }

  getPriorityOptions() {
    return [
      { value: 'all', label: 'All Priorities' },
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'critical', label: 'Critical' },
    ];
  }

  getAllAssignees(): string[] {
    // Prefer names fetched from the app-users API (scoped by appId).
    // Fall back to names already present on the loaded tasks when the API
    // hasn't returned yet or appId is not set.
    if (this.appUserNames.length > 0) {
      return this.appUserNames;
    }
    const set = new Set<string>();
    this.board.columns.forEach((col) =>
      col.tasks.forEach((task) => {
        if (task.assignee?.trim()) set.add(task.assignee);
      }),
    );
    return Array.from(set);
  }

  getPriorityLabel(priority: string): string {
    return (
      (
        {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          critical: 'Critical',
          all: 'All',
        } as any
      )[priority] || priority
    );
  }

  getPriorityColor(priority: Task['priority']): string {
    return (
      {
        low: '#7ED321',
        medium: '#F5A623',
        high: '#FF6B6B',
        critical: '#9013FE',
      } as any
    )[priority];
  }

  formatDate(date: Date | undefined): string {
    if (!date) return 'No due date';
    const diffDays = Math.ceil(
      (new Date(date).getTime() - Date.now()) / 86400000,
    );
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    return `In ${diffDays} days`;
  }

  isOverdue(date: Date | undefined): boolean {
    if (!date) return false;
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return Date.now() > d.getTime();
  }

  private getRandomColor(): string {
    const colors = [
      '#4A90E2',
      '#7ED321',
      '#F5A623',
      '#9013FE',
      '#FF6B6B',
      '#50E3C2',
      '#B8E986',
      '#BD10E0',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }
}
